import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import { articles } from "@/data/articles";
import { activeOffer } from "@/data/offer";
import { goPath } from "@/lib/golinks";
import { trackEvent, getStoredUTM } from "@/lib/tracking";

// /x — WHERE X AND SUBSTACK TRAFFIC LANDS.
//
// The articles are the studio's highest-quality asset and until now their
// traffic landed nowhere measurable: 1,999 posts, 529 with an outbound link,
// exactly one containing the string "utm_" — and that one was Substack's own
// share parameter, which identifies nothing of ours.
//
// So this page is built around two rules from the strategy, and no others:
//
//   · READING FIRST. Someone arriving from a post came to read, not to be
//     sold to. The essays get the room; nothing blinks, nothing interrupts.
//
//   · ONE TAGGED LINK. Each article footer carries exactly one, routed through
//     /go so the click is counted at our own door before any destination can
//     strip the parameter. That link is what makes scoreboard line 4 — "≥1
//     attributed conversion from tagged organic links" — a number rather than
//     a hope.
//
// The index itself carries one too, at the very bottom, after the reader has
// had the chance to want it.

const dateLabel = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

function Lead({ article }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link
        to={`/x/${article.id}`}
        onClick={() =>
          trackEvent("article_open", {
            title_id: article.id,
            slug: article.id,
            surface: "x",
            position: "lead",
          })
        }
        className="group block"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-7 md:gap-10 items-center">
          <div className="md:col-span-7">
            <div className="card-shrine rounded-sm overflow-hidden aspect-[3/2] bg-parchment-deep">
              <img
                src={article.hero_url}
                alt={article.title}
                className="w-full h-full object-cover transition-transform duration-1000 ease-breath group-hover:scale-[1.02]"
              />
            </div>
          </div>
          <div className="md:col-span-5">
            <p className="font-body text-[0.7rem] text-dharma-gold-deep tracking-widest2 uppercase">
              Latest · {dateLabel(article.published)}
            </p>
            <h2 className="font-display text-2xl md:text-4xl text-ink leading-[1.1] tracking-tightish mt-3 group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath text-balance">
              {article.title}
            </h2>
            <p className="font-serif italic text-ink-soft text-lg md:text-xl mt-4 leading-relaxed text-pretty">
              {article.pull_quote}
            </p>
            <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase mt-6 inline-flex items-center gap-1.5">
              Read the essay <ArrowRight size={12} />
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function Row({ article, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{
        duration: 0.8,
        delay: Math.min(index * 0.04, 0.24),
        ease: [0.22, 1, 0.36, 1],
      }}
    >
      <Link
        to={`/x/${article.id}`}
        onClick={() =>
          trackEvent("article_open", {
            title_id: article.id,
            slug: article.id,
            surface: "x",
            position: index + 1,
          })
        }
        className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 items-start py-7 border-t border-dharma-gold/15"
      >
        <div className="sm:col-span-3">
          <div className="rounded-sm overflow-hidden aspect-[3/2] bg-parchment-deep">
            <img
              src={article.hero_url}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-1000 ease-breath group-hover:scale-[1.03]"
            />
          </div>
        </div>
        <div className="sm:col-span-9">
          <p className="font-body text-[0.68rem] text-ink-mute tracking-widest2 uppercase">
            {article.subtitle}
          </p>
          <h3 className="font-display text-xl md:text-2xl text-ink leading-tight tracking-tightish mt-1.5 group-hover:text-dharma-gold-deep transition-colors duration-700 ease-breath">
            {article.title}
          </h3>
          <p className="font-serif text-ink-soft text-base md:text-lg mt-2 leading-relaxed max-w-prose">
            {article.pull_quote}
          </p>
          <p className="font-body text-[0.68rem] text-ink-mute/80 tracking-widest2 uppercase mt-3">
            {dateLabel(article.published)}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}

export default function XLanding() {
  const offer = activeOffer();
  // The index's own single tagged link. Substack is the honest utm_source here:
  // it is the placement whose click brought most of this page's readers.
  const magnetHref = goPath("magnet-bio-substack") || "/offer";

  const sorted = [...articles].sort(
    (a, b) => new Date(b.published || 0) - new Date(a.published || 0)
  );
  const [lead, ...rest] = sorted;

  useEffect(() => {
    const utm = getStoredUTM();
    trackEvent("view_content", {
      title_id: "x",
      slug: "x",
      surface: "x",
      article_count: articles.length,
      arrived_via: utm.utm_content || null,
    });
  }, []);

  return (
    <PageShell title="Essays">
      <section className="max-w-6xl mx-auto px-5 md:px-10 lg:px-14 pt-10 md:pt-16 pb-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="eyebrow mb-5">Essays</div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tightish text-ink leading-[1.05] max-w-4xl text-balance">
            One scripture at a time, slowly.
          </h1>
          <p className="font-serif text-xl md:text-2xl text-ink-soft mt-6 max-w-2xl leading-relaxed text-pretty">
            Long-form essays on the world's sacred texts — what the story is
            actually saying, and why it has survived being retold for two
            thousand years.
          </p>
        </motion.div>
        <div className="rule-gold mt-10 md:mt-12" />
      </section>

      {lead && (
        <section className="max-w-6xl mx-auto px-5 md:px-10 lg:px-14 pb-12 md:pb-16">
          <Lead article={lead} />
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 md:px-10 lg:px-14 pb-16 md:pb-24">
        {rest.map((a, i) => (
          <Row key={a.id} article={a} index={i} />
        ))}
      </section>

      {/* The index's one tagged outbound. Last, on purpose: a reader who has
          scrolled the whole catalogue has already told us they want more. */}
      <section className="bg-parchment/40 border-t border-dharma-gold/20 py-14 md:py-20">
        <div className="max-w-2xl mx-auto px-5 md:px-10 lg:px-14 text-center">
          <p className="eyebrow mb-4">{offer.eyebrow}</p>
          <h2 className="font-display text-2xl md:text-4xl text-ink leading-tight tracking-tightish text-balance">
            {offer.title}
          </h2>
          <p className="font-serif text-ink-soft text-lg md:text-xl mt-4 leading-relaxed text-pretty">
            {offer.subtitle}
          </p>
          <a
            href={magnetHref}
            onClick={() =>
              trackEvent("magnet_link_click", {
                title_id: offer.sku,
                slug: offer.id,
                offer_id: offer.id,
                surface: "x",
                href: magnetHref,
              })
            }
            className="btn-pill btn-pill-solid mt-8"
          >
            {offer.deliverable.label}
            <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </PageShell>
  );
}
